<template>
	<section
		class="settings-section secondary-voicemail-section new-settings__section"
		aria-labelledby="secondary-voicemail-section-title"
	>
		<header class="new-settings__section-header">
			<div class="new-settings__section-header-row">
				<div class="new-settings__section-header-text">
					<h1
						id="secondary-voicemail-section-title"
						class="new-settings__section-title"
					>
						{{ strings.title }}
					</h1>
					<p class="new-settings__section-subtitle">
						{{ strings.subtitle }}
					</p>
				</div>
				<div
					v-if="!isPartnerFormLoading"
					class="btn-pill-group new-settings__section-actions secondary-voicemail-section__header-actions"
				>
					<!-- <button
						type="button"
						class="settings-section__icon-btn"
						aria-label="Help"
						data-tooltip="Help"
						data-position="bottom-left"
						@click.stop.prevent="onClickInfo"
					>
						<font-awesome-icon
							:icon="['far', 'question-circle']"
						/>
					</button>
					<button
						type="button"
						class="settings-section__icon-btn"
						aria-label="Copy settings"
						data-tooltip="Copy Settings"
						data-position="bottom-left"
						@click.stop.prevent="copyVoicemailData"
					>
						<font-awesome-icon :icon="['far', 'address-card']" />
					</button> -->
					<template v-if="isEditing">
						<button
							type="button"
							class="btn-pill btn-pill--sm"
							@click="cancelEditing"
						>
							{{ common.cancel }}
						</button>
						<button
							type="button"
							class="btn-pill btn-pill--sm"
							:disabled="isSaveDisabled"
							@click="onSaveClick"
						>
							{{ common.save }}
						</button>
					</template>
					<button
						v-else
						type="button"
						class="btn-pill btn-pill--sm"
						@click="startEditing"
					>
						{{ common.edit }}
					</button>
				</div>
			</div>
		</header>

		<div class="settings-section__content">
			<engage-loader v-if="isPartnerFormLoading" overlay />

			<form class="settings-section__card" @submit.prevent="onSaveClick">
				<div class="settings-section__card-body">
					<div
						class="settings-section__field-row settings-section__field-row--thirds"
					>
						<div class="settings-section__field">
							<label class="settings-section__label">
								{{ strings.fields.voicemailRetrievalNumber }}
							</label>
							<div
								class="settings-section__readonly settings-section__readonly--highlight"
							>
								<template v-if="partner">
									{{
										partner.global_rsm_phone_number
											| newFormatPhoneNumber
									}}
								</template>
							</div>
						</div>
						<div class="settings-section__field">
							<label class="settings-section__label">
								{{ strings.fields.mailboxNumber }}
							</label>
							<div
								class="settings-section__readonly settings-section__readonly--highlight"
							>
								<template v-if="partner">
									{{
										partner.twilio_phone_number
											| newFormatPhoneNumber
									}}
								</template>
							</div>
						</div>
						<div class="settings-section__field">
							<label
								for="sv-passcode"
								class="settings-section__label"
							>
								{{ common.password }}
							</label>
							<div class="settings-section__passcode-wrap">
								<input
									id="sv-passcode"
									v-model="form.passcode"
									autocomplete="new-password"
									:type="showPasscode ? 'text' : 'password'"
									class="settings-section__input"
									:placeholder="placeholders.password"
									maxlength="6"
									:readonly="!isEditing"
									@input="validatePasscode"
								/>
								<button
									v-if="form.passcode"
									type="button"
									class="settings-section__passcode-toggle"
									:data-tooltip="
										showPasscode
											? common.hidePassword
											: common.showPassword
									"
									@click="showPasscode = !showPasscode"
								>
									<font-awesome-icon
										:icon="[
											'fas',
											showPasscode ? 'eye-slash' : 'eye',
										]"
									/>
								</button>
							</div>
							<p
								v-if="passcodeErrorMessage"
								class="settings-section__error"
							>
								{{ passcodeErrorMessage }}
							</p>
						</div>
					</div>

					<div class="settings-section__field">
						<label
							for="sv-email_notifications"
							class="settings-section__label"
						>
							{{ strings.fields.emailNotifications }}
						</label>
						<div class="settings-section__input-tag-container">
							<div
								class="settings-section__tag-container"
								:class="{
									empty: fallbackEmailTags.length === 0,
								}"
							>
								<div
									v-for="(email, index) in fallbackEmailTags"
									:key="`${email}-${index}`"
									class="settings-section__tag"
								>
									{{ email }}
									<span
										v-if="isEditing"
										class="close"
										@click="removeEmail(index)"
										>x</span
									>
								</div>
								<input
									id="sv-email_notifications"
									v-model="newEmail"
									type="email"
									class="settings-section__tag-input"
									:placeholder="
										fallbackEmailTags.length === 0
											? strings.placeholders.addEmail
											: strings.placeholders.addEmails
									"
									:readonly="!isEditing"
									@keydown.enter.prevent="addEmail"
									@keydown="handleBackspace"
									@keypress="checkForComma"
									@input="debounceFilterEmailSuggestions"
									@blur="addEmail"
								/>
							</div>
							<ul
								v-if="emailSuggestions.length && isEditing"
								:class="[
									'settings-section__email-suggestions',
									{
										'open-upwards': shouldOpenUpwards,
									},
								]"
							>
								<li
									v-for="suggestion in emailSuggestions"
									:key="suggestion"
									class="settings-section__suggestion-item"
									@mousedown="selectEmail(suggestion)"
								>
									{{ suggestion }}
								</li>
							</ul>
						</div>
						<p
							v-if="emailErrorMessage"
							class="settings-section__error"
						>
							{{ emailErrorMessage }}
						</p>
					</div>
				</div>
			</form>
		</div>
	</section>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import intercomConfig from '@/config/intercom'
import EngageLoader from '@/components/EngageLoader.vue'
import {
	SECONDARY_VOICEMAIL_STRINGS,
	SETTINGS_COMMON,
	SETTINGS_PLACEHOLDERS,
	SETTINGS_VALIDATION,
} from '@/config/settingsConstants'

/**
 * Empty partner settings form shape.
 *
 * @return {Object}
 */
function getSettingsFormObject() {
	return {
		name: '',
		wma_display_name: '',
		wma_web_link: '',
		office_phone_number: '',
		office_fax_number: '',
		ob_caller_id_number: '',
		address_1: '',
		address_2: '',
		city: '',
		state_province: '',
		country: '',
		postal_code: '',
		fallback_email: '',
		fallback_phone_number: '',
		timezone: '',
		rsm_fallback_email: '',
		passcode: '',
		ehr_config: {
			username: '',
			password: '',
			destination_id: '',
			service_application_id: '',
			service_facility_id: '',
			note_types: '',
			metadata: '',
		},
	}
}

/**
 * Secondary voicemail settings — mailbox, passcode, and notifications.
 */
export default {
	components: {
		EngageLoader,
	},

	inject: {
		ensureSettingsPartnerLoaded: { default: null },
	},

	computed: {
		fallbackEmailTags: {
			get() {
				const emails = this.form.rsm_fallback_email
				if (emails && emails.trim()) {
					return emails.split(/,|;|\s+/).filter(email => email.trim())
				}
				return []
			},
			set(newEmails) {
				this.form.rsm_fallback_email = newEmails.join(', ')
			},
		},

		isSaveDisabled() {
			if (!this.partner) {
				return true
			}
			if (!this.lastSavedSnapshot) {
				return true
			}
			return !this.hasUnsavedChanges
		},

		hasUnsavedChanges() {
			if (!this.lastSavedSnapshot) {
				return false
			}
			return this.lastSavedSnapshot !== this.getCurrentSnapshot()
		},

		shouldOpenUpwards() {
			return this.emailSuggestions.length > 2
		},

		partnerId() {
			return parseInt(this.$route.params.partner, 10)
		},

		...mapGetters({
			partner: 'partners/active',
		}),
	},

	methods: {
		fields() {
			return Object.keys(this.form)
		},

		fillForm() {
			if (!this.partner) {
				return this.resetFormFields()
			}

			this.fields().forEach(key => {
				if (key === 'ehr_config') {
					return
				}

				if (
					this.partner[key] !== null &&
					typeof this.partner[key] !== 'undefined'
				) {
					if (
						[
							'office_phone_number',
							'office_fax_number',
							'fallback_phone_number',
							'ob_caller_id_number',
						].includes(key)
					) {
						this.form[key] = this.partner[key].replace(/^\+1/, '')
					} else {
						this.form[key] = this.partner[key]
					}
				}
			})

			if (this.partner.ehr_settings) {
				this.form.ehr_config.username =
					this.partner.ehr_settings.username || ''
				this.form.ehr_config.password =
					this.partner.ehr_settings.password || ''
				this.form.ehr_config.destination_id =
					this.partner.ehr_settings.destination_id || ''
				this.form.ehr_config.service_application_id =
					this.partner.ehr_settings.service_application_id || ''
				this.form.ehr_config.service_facility_id =
					this.partner.ehr_settings.service_facility_id || ''
				this.form.ehr_config.metadata =
					this.partner.ehr_settings.metadata || {}
			}

			this.officeNumberErrorMessage = ''
			this.passcodeErrorMessage = ''
			this.fallbackNumberErrorMessage = ''
			this.emailErrorMessage = ''
			this.showPasscode = false
			this.form.rsm_fallback_email =
				this.partner.rsm_fallback_email ||
				this.partner.fallback_email ||
				''
		},

		resetFormFields() {
			this.form = getSettingsFormObject()
		},

		getNoteTypesPayloadForSave() {
			const raw = this.partner?.ehr_settings?.note_types
			if (!raw) {
				return []
			}
			try {
				return JSON.parse(raw)
			} catch (e) {
				return []
			}
		},

		findDuplicates(emails) {
			const duplicates = []
			const emailMap = new Map()

			emails.forEach(email => {
				const normalizedEmail = email.toLowerCase()
				if (emailMap.has(normalizedEmail)) {
					duplicates.push(email)
				} else {
					emailMap.set(normalizedEmail, true)
				}
			})
			return duplicates
		},

		async savePartnerSettings() {
			if (this.isSaveDisabled) {
				return false
			}

			if (!this.validatePasscode()) {
				this.$alert.error(this.passcodeErrorMessage)
				return false
			}

			if (!this.validateOfficePhoneNumber()) {
				this.$alert.error(this.officeNumberErrorMessage)
				return false
			}

			if (!this.validateFallbackPhoneNumber()) {
				this.$alert.error(this.fallbackNumberErrorMessage)
				return false
			}

			if (!this.form.rsm_fallback_email && this.form.fallback_email) {
				this.form.rsm_fallback_email = this.form.fallback_email
			}

			if (this.form.rsm_fallback_email) {
				const emails = this.form.rsm_fallback_email
					.split(/,|;|\n|\s/)
					.map(email => email.trim())
					.filter(email => email)
				const duplicates = this.findDuplicates(emails)
				if (duplicates.length > 0) {
					this.$alert.error(
						SETTINGS_VALIDATION.duplicateEmails(duplicates)
					)
					return false
				}
				this.form.rsm_fallback_email = emails.join(', ')
			}

			const payload = {
				partner: {
					id: this.partner.id,
					name: this.form.name,
					wma_display_name: this.form.wma_display_name,
					wma_web_link: this.form.wma_web_link,
					office_phone_number: this.form.office_phone_number,
					office_fax_number: this.form.office_fax_number,
					ob_caller_id_number: this.form.ob_caller_id_number,
					address_1: this.form.address_1,
					address_2: this.form.address_2,
					city: this.form.city,
					state_province: this.form.state_province,
					postal_code: this.form.postal_code,
					rsm_fallback_email: this.form.rsm_fallback_email,
					country: this.form.country,
					fallback_email: this.form.fallback_email,
					fallback_phone_number: this.form.fallback_phone_number,
					timezone: this.form.timezone,
					passcode: this.form.passcode,
				},
				ehr_config: {
					username: this.form.ehr_config.username,
					password: this.form.ehr_config.password,
					destination_id: this.form.ehr_config.destination_id,
					service_application_id: this.form.ehr_config
						.service_application_id,
					service_facility_id: this.form.ehr_config
						.service_facility_id,
					note_types: this.getNoteTypesPayloadForSave(),
					metadata: this.form.ehr_config.metadata,
				},
			}

			try {
				await this.update({
					id: this.partner.id,
					fromSettings: true,
					payload,
				})
				this.$alert.success('partners.success')
				this.takeSavedSnapshot()
				this.showPasscode = false
				return true
			} catch (e) {
				this.$alert.error('partners.error')
				return false
			}
		},

		async initializeSettingsPartnerForm() {
			await this.loadPartnerForm()
		},

		async loadPartnerForm({ force = false } = {}) {
			try {
				this.isPartnerFormLoading = true

				if (this.ensureSettingsPartnerLoaded) {
					await this.ensureSettingsPartnerLoaded(force)
					this.fillForm()
					this.takeSavedSnapshot()
					return
				}

				await this.getPartnerData()
			} finally {
				this.isPartnerFormLoading = false
			}
		},

		async refreshSettingsPartner() {
			if (this.ensureSettingsPartnerLoaded) {
				await this.ensureSettingsPartnerLoaded(true)
				this.fillForm()
				this.takeSavedSnapshot()
				return
			}

			await this.getPartnerData()
		},

		removeLastEmail() {
			if (this.fallbackEmailTags.length > 0) {
				this.fallbackEmailTags.pop()
				this.updateFormData()
			}
		},

		handleBackspace(event) {
			if (
				(event.key === 'Backspace' || event.which === 8) &&
				!this.newEmail
			) {
				this.removeLastEmail()
			}
		},

		updateFormData() {
			this.form.rsm_fallback_email = this.fallbackEmailTags.join(', ')
		},

		removeEmail(index) {
			const emails = this.fallbackEmailTags.filter((_, i) => i !== index)
			this.fallbackEmailTags = emails
			if (emails.length === 0) {
				this.form.rsm_fallback_email = null
				this.form.rsm_fallback_email = emails.join(', ')
			}
		},

		addEmail() {
			const emails = this.newEmail.split(/[;, \t\n]+/)
			emails.forEach(email => {
				if (this.validateEmail(email)) {
					const trimmedEmail = email.trim()
					if (trimmedEmail) {
						const newEmails = trimmedEmail.split(/[;, \t\n]+/)
						const existingEmails = (
							this.form.rsm_fallback_email || ''
						)
							.trim()
							.split(/[;, \t\n]+/)
							.filter(item => item.trim())
						const allEmails = [...existingEmails, ...newEmails]
						this.form.rsm_fallback_email = allEmails.join(', ')
					}
				}
			})
			this.newEmail = ''
			this.emailSuggestions = []
		},

		async fetchEmailSuggestions(searchTerm) {
			if (!searchTerm) {
				return
			}

			const partnerId = this.partnerId
			const apiUrl = `${process.env.VUE_APP_API_URL}/api/v4/partners/${partnerId}/search?field=email&role=office_manager&search_value=${searchTerm}`

			try {
				const response = await fetch(apiUrl, {
					headers: {
						Authorization: `Bearer ${this.$store.state.auth.session.access_token}`,
					},
				})

				if (!response.ok) {
					throw new Error('Failed to fetch email suggestions')
				}

				const data = await response.json()
				const fallbackEmails = this.form.rsm_fallback_email
					.split(',')
					.map(email => email.trim())

				this.emailSuggestions = data
					.map(suggestion => suggestion.trim())
					.filter(email => !fallbackEmails.includes(email))
			} catch (error) {
				console.error(
					'fetchEmailSuggestions: Error fetching email suggestions',
					error
				)
			}
		},

		validateEmail(email) {
			if (!email) {
				this.emailErrorMessage = ''
				return true
			}

			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
			if (!emailRegex.test(email)) {
				this.emailErrorMessage = SETTINGS_VALIDATION.emailInvalid
				return false
			}

			if (this.fallbackEmailTags.includes(email)) {
				this.emailErrorMessage = SETTINGS_VALIDATION.emailDuplicate
				return false
			}

			if (/[A-Z]/.test(email)) {
				this.emailErrorMessage =
					SETTINGS_VALIDATION.emailNoCapitalLetters
				return false
			}

			this.emailErrorMessage = ''
			return true
		},

		checkForComma(event) {
			if (event.key === ',') {
				event.preventDefault()
				this.addEmail()
			}
		},

		async selectEmail(suggestion) {
			if (this.validateEmail(suggestion)) {
				const newEmails = suggestion.split(/[;, \t\n]+/)
				const existingEmails = (this.form.rsm_fallback_email || '')
					.trim()
					.split(/[;, \t\n]+/)
					.filter(email => email.trim())
				const allEmails = [...existingEmails, ...newEmails]
				this.form.rsm_fallback_email = allEmails.join(', ')
				this.newEmail = ''
				this.emailErrorMessage = ''
			}
		},

		debounce(fn, delay) {
			let timeoutID
			return function debounced(...args) {
				if (timeoutID) {
					clearTimeout(timeoutID)
				}
				timeoutID = setTimeout(() => {
					fn.apply(this, args)
				}, delay)
			}
		},

		debounceFilterEmailSuggestions(event) {
			const searchTerm = event.target.value.trim()
			this.debounce(function filterSuggestions() {
				if (searchTerm.length >= 2) {
					this.fetchEmailSuggestions(searchTerm)
				} else {
					this.emailSuggestions = []
				}
			}, 300).apply(this)
		},

		isSequential(passcode) {
			for (let i = 0; i < passcode.length - 2; i++) {
				if (
					parseInt(passcode[i], 10) + 1 ===
						parseInt(passcode[i + 1], 10) &&
					parseInt(passcode[i + 1], 10) + 1 ===
						parseInt(passcode[i + 2], 10)
				) {
					return true
				}
			}
			return false
		},

		isRepeating(passcode) {
			for (let i = 0; i < passcode.length - 2; i++) {
				if (
					passcode[i] === passcode[i + 1] &&
					passcode[i + 2] === passcode[i]
				) {
					return true
				}
			}
			return false
		},

		validatePasscode() {
			const passcode = this.form.passcode

			if (!passcode) {
				this.passcodeErrorMessage = ''
				return true
			}

			if (!/^\d+$/.test(passcode)) {
				this.passcodeErrorMessage =
					SETTINGS_VALIDATION.passcodeDigitsOnly
				return false
			}

			if (passcode.length < 6) {
				this.passcodeErrorMessage =
					SETTINGS_VALIDATION.passcodeMinLength
				return false
			}

			if (this.isSequential(passcode)) {
				this.passcodeErrorMessage =
					SETTINGS_VALIDATION.passcodeNoSequential
				return false
			}

			if (this.isRepeating(passcode)) {
				this.passcodeErrorMessage =
					SETTINGS_VALIDATION.passcodeNoRepeating
				return false
			}

			this.passcodeErrorMessage = ''
			return true
		},

		validateOfficePhoneNumber() {
			const sanitizedPhoneNumber = this.form.office_phone_number.replace(
				/\D/g,
				''
			)

			const sanitizedTwilioPhone = (
				this.partner?.twilio_phone_number || ''
			)
				.replace(/^\+1/, '')
				.replace(/\D/g, '')

			if (sanitizedPhoneNumber === '') {
				this.officeNumberErrorMessage = ''
				return true
			}

			if (!/^[2-9]\d{9}$/.test(sanitizedPhoneNumber)) {
				this.officeNumberErrorMessage =
					SETTINGS_VALIDATION.phoneTenDigits
				return false
			}

			if (
				sanitizedTwilioPhone &&
				sanitizedPhoneNumber === sanitizedTwilioPhone
			) {
				this.officeNumberErrorMessage =
					SETTINGS_VALIDATION.phoneSameAsTwilio
				return false
			}

			this.officeNumberErrorMessage = ''
			return true
		},

		validateFallbackPhoneNumber() {
			const sanitizedPhoneNumber = this.form.fallback_phone_number.replace(
				/\D/g,
				''
			)

			const sanitizedTwilioPhone = (
				this.partner?.twilio_phone_number || ''
			)
				.replace(/^\+1/, '')
				.replace(/\D/g, '')

			const sanitizedOfficePhone = this.form.office_phone_number
				.replace(/^\+1/, '')
				.replace(/\D/g, '')

			if (sanitizedPhoneNumber === '') {
				this.fallbackNumberErrorMessage = ''
				return true
			}

			if (!/^[2-9]\d{9}$/.test(sanitizedPhoneNumber)) {
				this.fallbackNumberErrorMessage =
					SETTINGS_VALIDATION.phoneTenDigits
				return false
			}

			if (
				sanitizedTwilioPhone &&
				sanitizedPhoneNumber === sanitizedTwilioPhone
			) {
				this.fallbackNumberErrorMessage =
					SETTINGS_VALIDATION.phoneSameAsTwilioOrOffice
				return false
			}

			if (sanitizedPhoneNumber === sanitizedOfficePhone) {
				this.fallbackNumberErrorMessage =
					SETTINGS_VALIDATION.phoneSameAsTwilioOrOffice
				return false
			}

			this.fallbackNumberErrorMessage = ''
			return true
		},

		copyVoicemailData() {
			if (!this.form.rsm_fallback_email || !this.validatePasscode()) {
				this.$alert.error(this.strings.copySettingsValidationError)
				return
			}

			const formattedGlobalRsmPhoneNumber = this.partner.global_rsm_phone_number
				.substring(2)
				.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
			const formattedTwilioPhoneNumber = this.partner.twilio_phone_number
				.substring(2)
				.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')

			const { instructions } = SECONDARY_VOICEMAIL_STRINGS
			const voicemailData = `
${instructions.title}

${instructions.intro}

${instructions.importantNotesHeading}
${instructions.noteMultipleUsers}
${instructions.noteNoPasscodeSupport}
${instructions.noteNotInDashboard}

${instructions.configurationHeading}

${instructions.configurationBody}

${instructions.retrieveHeading}
${instructions.stepDial(formattedGlobalRsmPhoneNumber)}
${instructions.stepMailbox(formattedTwilioPhoneNumber)}
${instructions.stepPasscode(this.form.passcode)}
${instructions.stepReplay}
${instructions.stepNoRewind}
                `

			navigator.clipboard
				.writeText(voicemailData)
				.then(() => {
					this.$alert.success(this.strings.copySuccess)
				})
				.catch(err => {
					console.error('An error occurred:', err)
					this.$alert.error(this.strings.copyError)
				})
		},

		onClickInfo() {
			if (window.Intercom && intercomConfig.RSM_ARTICLE_ID) {
				window.Intercom('showArticle', intercomConfig.RSM_ARTICLE_ID)
			}
		},

		async getPartnerData() {
			if (!this.partner?.id) {
				return
			}
			try {
				await this.getPartner(this.partner.id)
			} catch (e) {
				this.$alert.error('default.error')
			} finally {
				this.fillForm()
				this.takeSavedSnapshot()
			}
		},

		getCurrentSnapshot() {
			const normalizedEmails = (this.form.rsm_fallback_email || '')
				.split(',')
				.map(e => e.trim())
				.filter(Boolean)
				.join(', ')

			return JSON.stringify({
				form: {
					...this.form,
					rsm_fallback_email: normalizedEmails,
				},
				fallbackEmailTags: [...this.fallbackEmailTags],
			})
		},

		takeSavedSnapshot() {
			this.lastSavedSnapshot = this.getCurrentSnapshot()
		},

		restoreFromSnapshot() {
			if (!this.lastSavedSnapshot) {
				return
			}
			const snapshot = JSON.parse(this.lastSavedSnapshot)
			this.form = { ...getSettingsFormObject(), ...snapshot.form }
			if (snapshot.fallbackEmailTags) {
				this.form.rsm_fallback_email = snapshot.fallbackEmailTags.join(
					', '
				)
			}
		},

		startEditing() {
			this.isEditing = true
		},

		cancelEditing() {
			this.restoreFromSnapshot()
			this.isEditing = false
		},

		async onSaveClick() {
			if (!this.form.passcode) {
				this.passcodeErrorMessage = SETTINGS_VALIDATION.passcodeRequired
				this.$alert.error(this.passcodeErrorMessage)
				return
			}

			if (!this.validatePasscode()) {
				this.$alert.error(this.passcodeErrorMessage)
				return
			}

			const saved = await this.savePartnerSettings()
			if (saved) {
				this.isEditing = false
				await this.refreshSettingsPartner()
			}
		},

		cancelAllEditing() {
			if (this.isEditing) {
				this.cancelEditing()
			}
		},

		...mapActions({
			update: 'partners/update',
			getPartner: 'partners/find',
		}),
	},

	name: 'SecondaryVoicemailSection',

	watch: {
		partner: {
			immediate: true,
			handler(newPartner, oldPartner) {
				if (!newPartner) {
					return
				}
				if (!oldPartner || newPartner.id !== oldPartner.id) {
					this.fillForm()
					if (!this.ensureSettingsPartnerLoaded) {
						this.$nextTick(() => {
							if (!this.lastSavedSnapshot) {
								this.takeSavedSnapshot()
							}
						})
					}
				}
			},
		},

		async 'partner.id'(newId, oldId) {
			if (!newId || newId === oldId || oldId == null) {
				return
			}

			this.isEditing = false
			await this.loadPartnerForm({ force: true })
		},
	},

	async created() {
		this.form = getSettingsFormObject()
		this.timezones = moment.tz.zonesForCountry('us')

		if (this.deferPartnerFormInit) {
			return
		}

		await this.initializeSettingsPartnerForm()
	},

	beforeDestroy() {
		this.cancelAllEditing()
	},

	deactivated() {
		this.cancelAllEditing()
	},

	data() {
		return {
			common: SETTINGS_COMMON,
			strings: SECONDARY_VOICEMAIL_STRINGS,
			placeholders: SETTINGS_PLACEHOLDERS,
			form: getSettingsFormObject(),
			isPartnerFormLoading: false,
			timezones: [],
			lastSavedSnapshot: null,
			showPasscode: false,
			newEmail: '',
			passcodeErrorMessage: '',
			fallbackNumberErrorMessage: '',
			officeNumberErrorMessage: '',
			emailSuggestions: [],
			emailErrorMessage: '',
			isEditing: false,
		}
	},
}
</script>
